/**
 * capabilities.ts — what a given host can actually do.
 *
 * The renderer must never branch on `if (electron)` for *features*. It asks
 * `resolveCapabilities(host)` (surfaced as the `useCapability` hook) which
 * returns a flat boolean map. A feature checks the flag, renders the native
 * affordance when present, and falls back to the cloud/in-app equivalent when
 * absent.
 *
 * See Docs/architecture/26-electron-desktop-and-mobile-architecture.md §4.3.
 */

import type { OmniHost } from "./detect";

export interface CapabilityFlags {
  /** OS-level global hotkey wake (Spotlight). Electron only. */
  globalHotkey: boolean;
  /** Read/write the user's real filesystem. Electron full; mobile scoped; web none. */
  localFs: boolean;
  /** Spawn local stdio MCP servers (`child_process`). Electron only. */
  localMcp: boolean;
  /** Capture a screen / window. */
  screenCapture: boolean;
  /** Microphone capture (always available on a modern client). */
  micCapture: boolean;
  /** Camera capture. */
  cameraCapture: boolean;
  /** OS push notifications (APNs/FCM/Web Push/OS notification). */
  pushNotifications: boolean;
  /** Receive OS share intents (share-to-Omni). Mobile only. */
  shareTarget: boolean;
  /** Wake periodically in the background to tail the event log. */
  backgroundSync: boolean;
  /** Biometric unlock (FaceID / fingerprint / Hello / Touch ID). */
  biometricUnlock: boolean;
  /** WebRTC media tier available for the transport upgrade (§6). */
  webrtcMedia: boolean;
}

const ALL_FALSE: CapabilityFlags = {
  globalHotkey: false,
  localFs: false,
  localMcp: false,
  screenCapture: false,
  micCapture: false,
  cameraCapture: false,
  pushNotifications: false,
  shareTarget: false,
  backgroundSync: false,
  biometricUnlock: false,
  webrtcMedia: false,
};

/** True when the current document can use `getUserMedia` / `getDisplayMedia`. */
function hasMediaDevices(): boolean {
  return typeof navigator !== "undefined" && !!navigator.mediaDevices;
}

function hasDisplayMedia(): boolean {
  return hasMediaDevices() && typeof navigator.mediaDevices.getDisplayMedia === "function";
}

function hasWebRtc(): boolean {
  return typeof RTCPeerConnection !== "undefined";
}

function hasWebPush(): boolean {
  return typeof window !== "undefined" && "PushManager" in window && "serviceWorker" in navigator;
}

/**
 * Resolve the capability map for a host. Web capabilities are feature-detected
 * from the DOM; native hosts are granted the superset their bridge implements.
 */
export function resolveCapabilities(host: OmniHost): CapabilityFlags {
  const media = hasMediaDevices();

  switch (host) {
    case "electron":
      return {
        globalHotkey: true,
        localFs: true,
        localMcp: true,
        screenCapture: true,
        micCapture: true,
        cameraCapture: true,
        pushNotifications: true,
        shareTarget: false,
        backgroundSync: true,
        biometricUnlock: true,
        webrtcMedia: true,
      };

    case "ios":
    case "android":
      return {
        globalHotkey: false,
        localFs: true, // scoped (Files app / Storage Access Framework)
        localMcp: false,
        screenCapture: true, // ReplayKit / MediaProjection via native plugin
        micCapture: true,
        cameraCapture: true,
        pushNotifications: true,
        shareTarget: true,
        backgroundSync: true,
        biometricUnlock: true,
        webrtcMedia: true,
      };

    case "web":
    default:
      return {
        ...ALL_FALSE,
        localFs: typeof window !== "undefined" && "showOpenFilePicker" in window, // gated FS Access API
        screenCapture: hasDisplayMedia(),
        micCapture: media,
        cameraCapture: media,
        pushNotifications: hasWebPush(),
        backgroundSync: typeof navigator !== "undefined" && "serviceWorker" in navigator,
        webrtcMedia: hasWebRtc(),
      };
  }
}
